import { faArrowDown, faArrowUp, faCopy, faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { Box, FormHelperText } from "@mui/material";
import type {
	ArrayFieldItemButtonsTemplateProps,
	ArrayFieldItemTemplateProps,
	ArrayFieldTemplateProps,
	FieldTemplateProps,
	ObjectFieldTemplateProps,
	WidgetProps,
} from "@rjsf/utils";
import dayjs from "dayjs";
import { AavoIconButton } from "src/components/common/buttons/AavoIconButton.tsx";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox.tsx";
import { AavoDatePicker } from "src/components/common/inputFields/AavoDatePicker.tsx";
import { AavoDateTimePicker } from "src/components/common/inputFields/AavoDateTimePicker.tsx";
import { AavoNumberField } from "src/components/common/inputFields/AavoNumberField.tsx";
import { AavoSelectField } from "src/components/common/inputFields/AavoSelectField.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import type { AavoTextFieldProps } from "src/components/common/inputFields/AavoTextField.tsx";
import i18n from "i18next";

export function AavoTextWidget(props: WidgetProps) {
	if (props.schema.type === "number" || props.schema.type === "integer") {
		return <AavoNumberWidget {...props} />;
	}
	return <AavoStringWidget {...props} />;
}

function AavoStringWidget({
	id,
	htmlName,
	label,
	hideLabel,
	value,
	required,
	disabled,
	readonly,
	autofocus,
	placeholder,
	rawErrors,
	schema,
	onChange,
	onBlur,
	onFocus,
	options,
	multiline,
	minRows,
	type,
}: WidgetProps & Pick<AavoTextFieldProps, "multiline" | "minRows" | "type">) {
	return (
		<AavoTextField
			id={id}
			name={htmlName ?? id}
			label={hideLabel ? undefined : label}
			value={value ?? ""}
			required={required}
			disabled={disabled || readonly}
			autoFocus={autofocus}
			placeholder={placeholder}
			error={errorMessage(rawErrors)}
			helperText={schema.description}
			multiline={multiline}
			minRows={minRows}
			type={type}
			submitOnBlur={false}
			onChange={(newValue) => onChange(newValue === "" ? options.emptyValue : newValue)}
			onBlur={() => onBlur(id, value)}
			onFocus={() => onFocus(id, value)}
		/>
	);
}

export function AavoTextareaWidget(props: WidgetProps) {
	return <AavoStringWidget {...props} multiline minRows={Number(props.options.rows ?? 4)} />;
}

export function AavoPasswordWidget(props: WidgetProps) {
	return <AavoStringWidget {...props} type={"password"} />;
}

export function AavoEmailWidget(props: WidgetProps) {
	return <AavoStringWidget {...props} type={"email"} />;
}

export function AavoUrlWidget(props: WidgetProps) {
	return <AavoStringWidget {...props} type={"url"} />;
}

function AavoNumberWidget({
	id,
	htmlName,
	label,
	hideLabel,
	value,
	required,
	disabled,
	readonly,
	autofocus,
	placeholder,
	rawErrors,
	schema,
	onChange,
	onBlur,
	onFocus,
	options,
}: WidgetProps) {
	return (
		<AavoNumberField
			id={id}
			name={htmlName ?? id}
			label={hideLabel ? undefined : label}
			value={
				typeof value === "number" ? value
				: value === "" || value == null ?
					null
				:	Number(value)
			}
			type={schema.type === "integer" ? "integer" : "decimal"}
			required={required}
			disabled={disabled || readonly}
			autoFocus={autofocus}
			placeholder={placeholder}
			error={errorMessage(rawErrors)}
			helperText={schema.description}
			submitOnBlur={false}
			slotProps={{
				htmlInput: {
					min: schema.minimum,
					max: schema.maximum,
					step: schema.multipleOf,
				},
			}}
			onChange={(newValue) => onChange(newValue ?? options.emptyValue)}
			onBlur={() => onBlur(id, value)}
			onFocus={() => onFocus(id, value)}
		/>
	);
}

export function AavoSelectWidget({
	id,
	label,
	hideLabel,
	value,
	required,
	disabled,
	readonly,
	autofocus,
	placeholder,
	rawErrors,
	schema,
	onChange,
	onBlur,
	onFocus,
	options,
}: WidgetProps) {
	const enumOptions = options.enumOptions ?? [];
	return (
		<AavoSelectField
			label={hideLabel ? "" : label}
			options={enumOptions}
			value={value ?? null}
			getOptionKey={(option) => option.value}
			getOptionLabel={(option) => option.label}
			disableClearable={false}
			disabled={disabled || readonly}
			autoFocus={autofocus}
			placeholder={placeholder}
			error={errorMessage(rawErrors)}
			helperText={schema.description}
			TextFieldProps={{ id, required }}
			onChange={(newValue) => onChange(newValue ?? options.emptyValue)}
			onBlur={() => onBlur(id, value)}
			onFocus={() => onFocus(id, value)}
		/>
	);
}

export function AavoCheckboxWidget({
	id,
	label,
	hideLabel,
	value,
	required,
	disabled,
	readonly,
	rawErrors,
	schema,
	onChange,
}: WidgetProps) {
	return (
		<AavoCheckbox
			id={id}
			label={hideLabel ? undefined : label}
			checked={Boolean(value)}
			required={required}
			disabled={disabled || readonly}
			error={errorMessage(rawErrors)}
			helperText={schema.description}
			onChange={(checked) => onChange(checked)}
		/>
	);
}

export function AavoDateWidget({
	id,
	label,
	hideLabel,
	value,
	required,
	disabled,
	readonly,
	rawErrors,
	schema,
	onChange,
	onBlur,
	options,
}: WidgetProps) {
	return (
		<AavoDatePicker
			label={hideLabel ? undefined : label}
			value={value == null || value === "" ? null : dayjs(value)}
			disabled={disabled || readonly}
			disableClearable={required}
			errorMessage={errorMessage(rawErrors)}
			slotProps={{
				textField: {
					id,
					required,
					helperText: errorMessage(rawErrors) ?? schema.description,
					onBlur: () => onBlur(id, value),
				},
			}}
			onChange={(date) => onChange(date?.format("YYYY-MM-DD") ?? options.emptyValue)}
		/>
	);
}

export function AavoDateTimeWidget({
	id,
	label,
	hideLabel,
	value,
	required,
	disabled,
	readonly,
	rawErrors,
	schema,
	onChange,
	onBlur,
	options,
}: WidgetProps) {
	return (
		<AavoDateTimePicker
			label={hideLabel ? undefined : label}
			value={value == null || value === "" ? null : dayjs(value)}
			disabled={disabled || readonly}
			errorMessage={errorMessage(rawErrors)}
			slotProps={{
				textField: {
					id,
					required,
					helperText: errorMessage(rawErrors) ?? schema.description,
					onBlur: () => onBlur(id, value),
				},
			}}
			onChange={(date) => onChange(date?.toISOString() ?? options.emptyValue)}
		/>
	);
}

export function AavoFieldTemplate({ children, hidden }: FieldTemplateProps) {
	return hidden ? <Box sx={{ display: "none" }}>{children}</Box> : children;
}

export function AavoObjectFieldTemplate({
	title,
	description,
	required,
	properties,
	fieldPathId,
}: ObjectFieldTemplateProps) {
	const isRoot = fieldPathId.path.length === 0;
	return (
		<Box
			component={isRoot ? "div" : "fieldset"}
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				minWidth: 0,
				margin: 0,
				padding: isRoot ? 0 : 2,
				border: isRoot ? 0 : 1,
				borderColor: "divider",
				borderRadius: 1,
			}}
		>
			{!isRoot && title && (
				<Box component={"legend"} sx={{ typography: "subtitle1", fontWeight: 600 }}>
					{requiredLabel(title, required)}
				</Box>
			)}
			{description && <FormHelperText>{description}</FormHelperText>}
			{properties.filter((property) => !property.hidden).map((property) => property.content)}
		</Box>
	);
}

export function AavoArrayFieldTemplate({
	title,
	required,
	items,
	canAdd,
	disabled,
	readonly,
	onAddClick,
	rawErrors,
	schema,
}: ArrayFieldTemplateProps) {
	return (
		<Box component={"fieldset"} sx={{ minWidth: 0, margin: 0, padding: 0, border: 0 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: 1 }}>
				<Box component={"legend"} sx={{ flex: 1, typography: "subtitle1", fontWeight: 600 }}>
					{requiredLabel(title, required)}
				</Box>
				{canAdd && (
					<AavoIconButton
						type={"button"}
						size={"small"}
						icon={faPlus}
						tooltip={i18n.t("add")}
						disabled={disabled || readonly}
						onClick={onAddClick}
					/>
				)}
			</Box>
			{schema.description && <FormHelperText sx={{ marginBottom: 1 }}>{schema.description}</FormHelperText>}
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>{items}</Box>
			<Errors errors={rawErrors} />
		</Box>
	);
}

export function AavoArrayFieldItemTemplate({ children, hasToolbar, buttonsProps }: ArrayFieldItemTemplateProps) {
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
			<Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
			{hasToolbar && <AavoArrayFieldItemButtonsTemplate {...buttonsProps} />}
		</Box>
	);
}

export function AavoArrayFieldItemButtonsTemplate({
	disabled,
	readonly,
	hasCopy,
	hasMoveDown,
	hasMoveUp,
	hasRemove,
	onCopyItem,
	onMoveDownItem,
	onMoveUpItem,
	onRemoveItem,
}: ArrayFieldItemButtonsTemplateProps) {
	const isDisabled = disabled || readonly;
	return (
		<Box sx={{ display: "flex", paddingTop: 0.5 }}>
			{(hasMoveUp || hasMoveDown) && (
				<AavoIconButton
					type={"button"}
					size={"small"}
					icon={faArrowUp}
					tooltip={i18n.t("move_up")}
					disabled={isDisabled || !hasMoveUp}
					onClick={onMoveUpItem}
				/>
			)}
			{(hasMoveUp || hasMoveDown) && (
				<AavoIconButton
					type={"button"}
					size={"small"}
					icon={faArrowDown}
					tooltip={i18n.t("move_down")}
					disabled={isDisabled || !hasMoveDown}
					onClick={onMoveDownItem}
				/>
			)}
			{hasCopy && (
				<AavoIconButton
					type={"button"}
					size={"small"}
					icon={faCopy}
					tooltip={i18n.t("copy")}
					disabled={isDisabled}
					onClick={onCopyItem}
				/>
			)}
			{hasRemove && (
				<AavoIconButton
					type={"button"}
					size={"small"}
					icon={faTrash}
					tooltip={i18n.t("remove")}
					disabled={isDisabled}
					onClick={onRemoveItem}
				/>
			)}
		</Box>
	);
}

function Errors({ errors }: { errors?: string[] }) {
	const message = errorMessage(errors);
	return message == null ? null : <FormHelperText error>{message}</FormHelperText>;
}

function errorMessage(errors: string[] | undefined): string | undefined {
	return errors?.join(" ");
}

function requiredLabel(label: string, required: boolean | undefined): string {
	return required ? `${label}*` : label;
}
